import type { DemoRequestResponse } from '@clareefai/types';
import { VideoModal } from '@clareefai/ui';
import { Award, Building, Calendar, Check, CheckCircle, ChevronDown, Clock, Copy, Eye, FileText, Globe, Loader, MessageSquare, MoreHorizontal, Package, Pencil, Phone, Plus, Search, Star, Target, UserPlus, Users, X, XCircle } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ReviewCard from '../../components/reviews/ReviewCard';
import UseCaseViewer from '../../components/shared/UseCaseViewer';
import { useAbility } from '../../contexts/AbilityContext';
import { useGetPromotersQuery } from '../../store/promoterApi';
import { useGetTeamMembersQuery } from '../../store/teamMemberApi';
import { useAssignDemoRequestMutation, useGetDemoRequestsQuery, useGetDemoRequestStatsQuery, useGetVendorMeQuery, useUpdateDemoRequestStatusMutation, useUpdateVendorMutation } from '../../store/vendorApi';
import { PUBLIC_SITE_URL } from '../../utils/publicSite';

const MyPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ability = useAbility();
  const canEdit = ability.can('update', 'Page');
  const { data: vendor, isLoading, error } = useGetVendorMeQuery();
  const [updateVendor] = useUpdateVendorMutation();
  const { data: promoters } = useGetPromotersQuery();
  const [activeTab, setActiveTab] = useState<'overview' | 'reviews' | 'usecases' | 'public' | 'requests'>('overview');
  const [copiedLink, setCopiedLink] = useState(false);
  const [reviewFilter, setReviewFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [googleReviewsUrlInput, setGoogleReviewsUrlInput] = useState('');
  const [g2ReviewsUrlInput, setG2ReviewsUrlInput] = useState('');
  const [urlSubmitting, setUrlSubmitting] = useState<'google' | 'g2' | null>(null);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionInput, setDescriptionInput] = useState('');
  const [descriptionSubmitting, setDescriptionSubmitting] = useState(false);
  const [isEditingAbout, setIsEditingAbout] = useState(false);
  const [aboutInput, setAboutInput] = useState('');
  const [aboutSubmitting, setAboutSubmitting] = useState(false);
  const pageIsDraft = !vendor?.pagePublished;
  const [pageStatusSaving, setPageStatusSaving] = useState(false);
  const [playingVideoUrl, setPlayingVideoUrl] = useState<string | null>(null);
  const [playingVideoTitle, setPlayingVideoTitle] = useState<string | undefined>(undefined);
  const [playingVideoDescription, setPlayingVideoDescription] = useState<string | undefined>(undefined);
  const [playingVideoTags, setPlayingVideoTags] = useState<string[]>([]);

  const { data: demoRequests = [], isLoading: requestsLoading } = useGetDemoRequestsQuery({});
  const { data: demoStats } = useGetDemoRequestStatsQuery();
  const { data: teamMembers = [] } = useGetTeamMembersQuery();
  const [assignDemoRequest, { isLoading: isAssigning }] = useAssignDemoRequestMutation();
  const [updateDemoRequestStatus, { isLoading: isUpdatingStatus }] = useUpdateDemoRequestStatusMutation();
  const [requestSearchTerm, setRequestSearchTerm] = useState('');
  const [requestStatusFilter, setRequestStatusFilter] = useState('all');
  const [requestSubTab, setRequestSubTab] = useState<'book_demo' | 'promoter_requests'>('book_demo');
  const [assignDropdownId, setAssignDropdownId] = useState<string | null>(null);
  const [statusDropdownId, setStatusDropdownId] = useState<string | null>(null);

  const companyData = {
    logo: vendor?.logo || undefined,
    name: vendor?.companyName || vendor?.name || 'Company Name',
    description: vendor?.description || '',
    about: vendor?.pageInfo?.about || '',
  };

  const solutions = vendor?.pageInfo?.products || [];

  const allReviews = React.useMemo(() => {
    if (!vendor?.reviews || vendor.reviews.length === 0) {
      return [];
    }
    return vendor.reviews.map(review => ({
      id: review.id,
      author: review.promoterName,
      company: review.promoterCompany || '',
      rating: review.rating || 0,
      comment: review.comment || '',
      product: review.productId || '',
      sector: '',
      companySize: '',
      hasVideo: review.hasVideo,
      hasImage: review.hasImage,
      videoUrl: review.videoUrl,
      videoTitle: review.videoTitle,
      videoDescription: review.videoDescription,
      videoTags: (review as any).videoTags || [],
      date: new Date(review.createdAt).toISOString().split('T')[0],
      featured: review.featured || false,
      verified: review.verified || false,
      helpful: 0,
      companyResponse: (review as { vendorResponse?: string | null }).vendorResponse ?? null
    }));
  }, [vendor?.reviews]);

  // Use Cases - Get from promoters' testimonials
  const useCases = useMemo(() => {
    if (!promoters) return [];

    return promoters.flatMap(promoter => {
      // DEV-166 — a published use case has isPublished === true (status becomes
      // PUBLISHED/FEATURED on publish, never APPROVED). The old `status === 'APPROVED'`
      // requirement hid every newly published use case, showing 0 instead of N.
      // Treat isPublished as the source of truth; keep the legacy statuses for old data.
      const useCaseTestimonials = (promoter.testimonials || []).filter(t => {
        if (t.type !== 'USE_CASE') return false;
        const isPublished = (t as any).isPublished === true;
        const publishedStatus = t.status === 'PUBLISHED' || t.status === 'FEATURED' || t.status === 'APPROVED';
        return isPublished || publishedStatus;
      });

      return useCaseTestimonials.map(testimonial => ({
        id: testimonial.id,
        promoterId: promoter.id,
        promoterName: `${promoter.firstName} ${promoter.lastName}`,
        companyName: promoter.company || 'Unknown Company',
        companySize: promoter.companySize || '',
        content: testimonial.content || '',
        createdAt: testimonial.createdAt,
        isFeatured: testimonial.isFeatured
      }));
    });
  }, [promoters]);

  const publicPageUrl = vendor?.pageInfo?.pageSlug
    ? `${PUBLIC_SITE_URL}/company/${vendor.pageInfo.pageSlug}`
    : `${typeof window !== 'undefined' ? window.location.origin : ''}/public-preview`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicPageUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleTogglePageStatus = async () => {
    if (!vendor?.email) return;
    setPageStatusSaving(true);
    try {
      await updateVendor({ email: vendor.email, pagePublished: pageIsDraft }).unwrap();
    } finally {
      setPageStatusSaving(false);
    }
  };

  const filteredReviews = allReviews.filter(review => {
    const matchesFilter = reviewFilter === 'all' ||
      (reviewFilter === 'featured' && review.featured) ||
      (reviewFilter === 'video' && review.hasVideo) ||
      (reviewFilter === 'verified' && review.verified);

    const matchesSearch = searchTerm === '' ||
      review.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
      review.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      review.comment.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  const reviewStats = {
    total: allReviews.length,
    featured: allReviews.filter(r => r.featured).length,
    video: allReviews.filter(r => r.hasVideo).length,
    verified: allReviews.filter(r => r.verified).length,
    averageRating: (allReviews.reduce((sum, r) => sum + r.rating, 0) / (allReviews.length || 1)).toFixed(1),
    withResponse: allReviews.filter(r => r.companyResponse).length
  };

  React.useEffect(() => {
    if (vendor?.pageInfo) {
      setGoogleReviewsUrlInput(vendor.pageInfo.googleReviewsUrl || '');
      setG2ReviewsUrlInput(vendor.pageInfo.g2ReviewsUrl || '');
    }
  }, [vendor?.pageInfo?.googleReviewsUrl, vendor?.pageInfo?.g2ReviewsUrl]);

  const handleSubmitDescription = async () => {
    if (!vendor) return;
    setDescriptionSubmitting(true);
    try {
      await updateVendor({ description: descriptionInput }).unwrap();
      setIsEditingDescription(false);
    } finally {
      setDescriptionSubmitting(false);
    }
  };

  const handleSubmitAbout = async () => {
    if (!vendor) return;
    setAboutSubmitting(true);
    try {
      await updateVendor({
        pageInfo: {
          ...vendor.pageInfo,
          about: aboutInput,
        },
      }).unwrap();
      setIsEditingAbout(false);
    } finally {
      setAboutSubmitting(false);
    }
  };

  const handleSubmitGoogleUrl = async () => {
    if (!vendor?.email) return;
    setUrlSubmitting('google');
    try {
      await updateVendor({
        email: vendor.email,
        pageInfo: {
          ...vendor.pageInfo,
          googleReviewsUrl: googleReviewsUrlInput || undefined,
          g2ReviewsUrl: vendor.pageInfo?.g2ReviewsUrl || undefined,
          about: vendor.pageInfo?.about,
          companyUrl: vendor.pageInfo?.companyUrl,
          products: vendor.pageInfo?.products,
        },
      }).unwrap();
    } finally {
      setUrlSubmitting(null);
    }
  };

  const handleSubmitG2Url = async () => {
    if (!vendor?.email) return;
    setUrlSubmitting('g2');
    try {
      await updateVendor({
        email: vendor.email,
        pageInfo: {
          ...vendor.pageInfo,
          googleReviewsUrl: vendor.pageInfo?.googleReviewsUrl || undefined,
          g2ReviewsUrl: g2ReviewsUrlInput || undefined,
          about: vendor.pageInfo?.about,
          companyUrl: vendor.pageInfo?.companyUrl,
          products: vendor.pageInfo?.products,
        },
      }).unwrap();
    } finally {
      setUrlSubmitting(null);
    }
  };

  const renderOverview = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {t('vendorPage.myPage.loadFailed')}
        </div>
      );
    }

    return (
      <div className="space-y-8">
        {/* Company Identity */}
        <div className="bg-white rounded-xl shadow-sm p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{t('vendorPage.myPage.companyIdentity')}</h2>
            {canEdit && (
              <button
                onClick={() => navigate('/settings', { state: { activeTab: 'profile' } })}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
              >
                <Pencil className="w-4 h-4" />
                <span>{t('vendorPage.myPage.editInSettings')}</span>
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <div className="text-center">
                {companyData.logo ? (
                  <img src={companyData.logo} alt="Logo" className="h-32 w-auto mx-auto mb-6 rounded-xl shadow-lg bg-white p-4" />
                ) : (
                  <div className="h-32 w-32 mx-auto mb-6 rounded-xl bg-gray-100 flex items-center justify-center">
                    <Building className="w-12 h-12 text-gray-400" />
                  </div>
                )}
                <h3 className="text-2xl font-bold text-gray-900">{companyData.name}</h3>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="space-y-4">
                {/* Inline-editable description */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">{t('vendorPage.myPage.description')}</span>
                    {canEdit && !isEditingDescription && (
                      <button
                        onClick={() => {
                          setDescriptionInput(vendor?.description || '');
                          setIsEditingDescription(true);
                        }}
                        className="p-1 text-gray-400 hover:text-blue-600 rounded transition-colors"
                        title={t('vendorPage.myPage.editDescription')}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  {isEditingDescription ? (
                    <div className="space-y-2">
                      <textarea
                        value={descriptionInput}
                        onChange={(e) => setDescriptionInput(e.target.value)}
                        rows={3}
                        placeholder={t('vendorPage.myPage.descriptionPlaceholder')}
                        className="w-full p-3 border border-blue-300 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        maxLength={5000}
                      />
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => setIsEditingDescription(false)}
                          className="p-1.5 text-gray-400 hover:text-gray-600 rounded transition-colors"
                          title={t('common.cancel')}
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <button
                          onClick={handleSubmitDescription}
                          disabled={descriptionSubmitting}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                        >
                          {descriptionSubmitting ? (
                            <Loader className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Check className="w-3.5 h-3.5" />
                          )}
                          {t('vendorPage.myPage.save')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-700 whitespace-pre-line min-h-[2rem]">
                      {companyData.description || <span className="text-gray-400 italic">{t('vendorPage.myPage.noDescription')}</span>}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Users className="w-4 h-4 flex-shrink-0" />
                  <span>{vendor?.companyName || vendor?.email || '—'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Solutions */}
        <div className="bg-white rounded-xl shadow-sm p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{t('vendorPage.myPage.ourSolutions')}</h2>
            {canEdit && (
              <button
                onClick={() => navigate('/settings', { state: { activeTab: 'products' } })}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>{t('vendorPage.myPage.add')}</span>
              </button>
            )}
          </div>

          {solutions.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">{t('vendorPage.myPage.noProducts')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {solutions.map((solution, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{solution.name}</h3>
                  <p className="text-gray-700 text-sm">{solution.description}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* About */}
        <div className="bg-white rounded-xl shadow-sm p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{t('vendorPage.myPage.about')}</h2>
            {canEdit && !isEditingAbout && (
              <button
                onClick={() => {
                  setAboutInput(vendor?.pageInfo?.about || '');
                  setIsEditingAbout(true);
                }}
                className="flex items-center space-x-2 px-3 py-1.5 text-sm text-gray-500 hover:text-blue-600 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors"
                title={t('vendorPage.myPage.editAbout')}
              >
                <Pencil className="w-3.5 h-3.5" />
                <span>{t('common.edit')}</span>
              </button>
            )}
          </div>

          {isEditingAbout ? (
            <div className="space-y-3">
              <textarea
                value={aboutInput}
                onChange={(e) => setAboutInput(e.target.value)}
                rows={6}
                placeholder={t('vendorPage.myPage.aboutPlaceholder')}
                className="w-full p-4 border border-blue-300 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                maxLength={5000}
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">{aboutInput.length}/5000</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsEditingAbout(false)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleSubmitAbout}
                    disabled={aboutSubmitting}
                    className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {aboutSubmitting ? (
                      <Loader className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Check className="w-3.5 h-3.5" />
                    )}
                    {t('vendorPage.myPage.save')}
                  </button>
                </div>
              </div>
            </div>
          ) : companyData.about ? (
            <p className="text-gray-700 whitespace-pre-line leading-relaxed">{companyData.about}</p>
          ) : (
            <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-xl">
              <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">{t('vendorPage.myPage.noAboutText')}</p>
              {canEdit && (
                <button
                  onClick={() => {
                    setAboutInput('');
                    setIsEditingAbout(true);
                  }}
                  className="mt-3 text-sm text-blue-600 hover:underline font-medium"
                >
                  {t('vendorPage.myPage.addOneNow')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderReviews = () => (
    <div className="space-y-6">
      {/* Review Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg p-4 text-center shadow-sm">
          <div className="text-2xl font-bold text-blue-600">{reviewStats.total}</div>
          <div className="text-sm text-gray-600">{t('vendorPage.myPage.reviews.total')}</div>
        </div>
        <div className="bg-white rounded-lg p-4 text-center shadow-sm">
          <div className="text-2xl font-bold text-yellow-600">{reviewStats.featured}</div>
          <div className="text-sm text-gray-600">{t('vendorPage.myPage.reviews.featured')}</div>
        </div>
        <div className="bg-white rounded-lg p-4 text-center shadow-sm">
          <div className="text-2xl font-bold text-purple-600">{reviewStats.video}</div>
          <div className="text-sm text-gray-600">{t('vendorPage.myPage.reviews.video')}</div>
        </div>
        <div className="bg-white rounded-lg p-4 text-center shadow-sm">
          <div className="text-2xl font-bold text-green-600">{reviewStats.verified}</div>
          <div className="text-sm text-gray-600">{t('vendorPage.myPage.reviews.verified')}</div>
        </div>
        <div className="bg-white rounded-lg p-4 text-center shadow-sm">
          <div className="text-2xl font-bold text-orange-600">{reviewStats.averageRating}</div>
          <div className="text-sm text-gray-600">{t('vendorPage.myPage.reviews.avgRating')}</div>
        </div>
        <div className="bg-white rounded-lg p-4 text-center shadow-sm">
          <div className="text-2xl font-bold text-indigo-600">{reviewStats.withResponse}</div>
          <div className="text-sm text-gray-600">{t('vendorPage.myPage.reviews.withResponse')}</div>
        </div>
      </div>

      {/* External Review Links - Add review on Google or G2 */}
      {canEdit && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('vendorPage.myPage.reviews.addOnGoogleOrG2')}</h3>
          <p className="text-sm text-gray-600 mb-4">{t('vendorPage.myPage.reviews.addReviewSubtitle')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Globe className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{t('vendorPage.myPage.reviews.googleReviews')}</h4>
                  {vendor?.pageInfo?.googleReviewsUrl && (
                    <a
                      href={vendor.pageInfo.googleReviewsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      {t('vendorPage.myPage.reviews.viewReviews')}
                    </a>
                  )}
                </div>
              </div>
              <input
                type="url"
                value={googleReviewsUrlInput}
                onChange={(e) => setGoogleReviewsUrlInput(e.target.value)}
                placeholder="https://g.page/r/..."
                className="w-full p-2 border border-gray-300 rounded text-sm mb-3"
              />
              <button
                onClick={handleSubmitGoogleUrl}
                disabled={urlSubmitting === 'google'}
                className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {urlSubmitting === 'google' ? <Loader className="w-4 h-4 animate-spin" /> : null}
                {t('vendorPage.myPage.reviews.submit')}
              </button>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Award className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{t('vendorPage.myPage.reviews.g2Reviews')}</h4>
                  {vendor?.pageInfo?.g2ReviewsUrl && (
                    <a
                      href={vendor.pageInfo.g2ReviewsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      {t('vendorPage.myPage.reviews.viewReviews')}
                    </a>
                  )}
                </div>
              </div>
              <input
                type="url"
                value={g2ReviewsUrlInput}
                onChange={(e) => setG2ReviewsUrlInput(e.target.value)}
                placeholder="https://www.g2.com/products/..."
                className="w-full p-2 border border-gray-300 rounded text-sm mb-3"
              />
              <button
                onClick={handleSubmitG2Url}
                disabled={urlSubmitting === 'g2'}
                className="w-full bg-orange-600 text-white py-2 rounded hover:bg-orange-700 transition-colors text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {urlSubmitting === 'g2' ? <Loader className="w-4 h-4 animate-spin" /> : null}
                {t('vendorPage.myPage.reviews.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <h3 className="text-lg font-semibold text-gray-900">{t('vendorPage.myPage.reviews.management')} ({filteredReviews.length})</h3>

          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('vendorPage.myPage.reviews.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            <select
              value={reviewFilter}
              onChange={(e) => setReviewFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="all">{t('vendorPage.myPage.reviews.allReviews')}</option>
              <option value="featured">{t('vendorPage.myPage.reviews.featuredFilter')}</option>
              <option value="video">{t('vendorPage.myPage.reviews.withVideo')}</option>
              <option value="verified">{t('vendorPage.myPage.reviews.verifiedFilter')}</option>
            </select>

            <button
              onClick={() => navigate('/reviews')}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>{t('vendorPage.myPage.add')}</span>
            </button>
          </div>
        </div>

        {/* Reviews List */}
        <div className="space-y-4">
          {filteredReviews.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              onPlayVideo={(url, title, description, tags) => {
                setPlayingVideoUrl(url);
                setPlayingVideoTitle(title);
                setPlayingVideoDescription(description);
                setPlayingVideoTags(tags || []);
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );

  const renderUseCases = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('vendorPage.myPage.useCases.title')}</h2>
          <p className="text-gray-600 mt-1">{t('vendorPage.myPage.useCases.publishedCount', { count: useCases.length })}</p>
        </div>
      </div>

      {useCases.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">{t('vendorPage.myPage.useCases.empty')}</p>
          <p className="text-sm text-gray-500 mt-1">{t('vendorPage.myPage.useCases.emptyHelp')}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {useCases.map((useCase) => (
            <div key={useCase.id} className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow">
              <UseCaseViewer
                content={useCase.content}
                promoterName={useCase.promoterName}
                companyName={useCase.companyName}
                companySize={useCase.companySize}
                createdAt={useCase.createdAt}
                showQuestionsAndAnswers={true}
              />
              {useCase.isFeatured && (
                <div className="mt-4 inline-flex items-center px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-sm font-medium">
                  <Award className="w-4 h-4 mr-1" />
                  {t('vendorPage.myPage.useCases.featuredUseCase')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderPublicPage = () => (
    <div className="space-y-6">
      {/* Link & sharing */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 sm:p-8 border border-blue-200">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('vendorPage.myPage.public.title')}</h2>
          <p className="text-gray-600">{t('vendorPage.myPage.public.subtitle')}</p>
        </div>

        <div className="bg-white rounded-lg p-6 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('vendorPage.myPage.public.pageUrl')}</label>
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={publicPageUrl}
              readOnly
              className="flex-1 p-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 text-sm"
            />
            <button
              onClick={handleCopyLink}
              className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium text-sm transition-colors whitespace-nowrap ${copiedLink
                  ? 'bg-green-600 text-white'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
            >
              {copiedLink ? (
                <>
                  <Check className="w-4 h-4" />
                  <span>{t('vendorPage.myPage.public.copied')}</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span>{t('vendorPage.myPage.public.copy')}</span>
                </>
              )}
            </button>
          </div>
          {!vendor?.pageInfo?.pageSlug && (
            <p className="text-xs text-amber-600 mt-2">
              {t('vendorPage.myPage.public.generatedFromName')}
            </p>
          )}
        </div>
      </div>

      {/* Page content summary - real data */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">{t('vendorPage.myPage.public.contentSummary')}</h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-5 bg-blue-50 rounded-xl border border-blue-100">
            <Star className="w-7 h-7 text-blue-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-blue-900">{reviewStats.averageRating}</div>
            <div className="text-xs text-blue-700">{t('vendorPage.myPage.public.avgRating')}</div>
          </div>
          <div className="text-center p-5 bg-green-50 rounded-xl border border-green-100">
            <MessageSquare className="w-7 h-7 text-green-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-green-900">{allReviews.length}</div>
            <div className="text-xs text-green-700">{t('vendorPage.myPage.public.reviewsCount')}</div>
          </div>
          <div className="text-center p-5 bg-purple-50 rounded-xl border border-purple-100">
            <Target className="w-7 h-7 text-purple-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-purple-900">{useCases.length}</div>
            <div className="text-xs text-purple-700">{t('vendorPage.myPage.public.useCasesCount')}</div>
          </div>
          <div className="text-center p-5 bg-orange-50 rounded-xl border border-orange-100">
            <Package className="w-7 h-7 text-orange-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-orange-900">{solutions.length}</div>
            <div className="text-xs text-orange-700">{t('vendorPage.myPage.public.solutionsCount')}</div>
          </div>
        </div>
      </div>

      {/* Mini preview */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">{t('vendorPage.myPage.public.pagePreview')}</h3>
          <button
            onClick={() => window.open(publicPageUrl, '_blank')}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
          >
            <Eye className="w-4 h-4" />
            <span>{t('vendorPage.myPage.public.openFullPreview')}</span>
          </button>
        </div>

        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white p-6">
            <div className="flex items-center space-x-4">
              {companyData.logo ? (
                <img src={companyData.logo} alt="Logo" className="h-14 w-14 object-contain bg-white rounded-xl p-2 shadow-md" />
              ) : (
                <div className="h-14 w-14 bg-white/10 rounded-xl flex items-center justify-center">
                  <Building className="w-7 h-7 text-white/60" />
                </div>
              )}
              <div>
                <h4 className="text-xl font-bold">{companyData.name}</h4>
                {companyData.description && (
                  <p className="text-blue-200 text-sm line-clamp-1 mt-1">{companyData.description}</p>
                )}
              </div>
              {allReviews.length > 0 && (
                <div className="ml-auto text-center hidden sm:block">
                  <div className="text-2xl font-bold">{reviewStats.averageRating}/5</div>
                  <div className="text-xs text-blue-200">{allReviews.length} {t('vendorPage.myPage.public.reviewsLabel')}</div>
                </div>
              )}
            </div>
          </div>

          <div className="p-6 bg-gray-50">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div className="bg-white rounded-lg p-3 border border-gray-100">
                <div className="text-lg font-bold text-green-600">{reviewStats.averageRating}/5</div>
                <div className="text-xs text-gray-500">{t('vendorPage.myPage.public.avgRatingLabel')}</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-gray-100">
                <div className="text-lg font-bold text-blue-600">{solutions.length}</div>
                <div className="text-xs text-gray-500">{t('vendorPage.myPage.public.solutionsListed')}</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-gray-100">
                <div className="text-lg font-bold text-purple-600">{useCases.length}</div>
                <div className="text-xs text-gray-500">{t('vendorPage.myPage.public.publishedUseCases')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const bookDemoRequests = useMemo(() =>
    demoRequests.filter((req: DemoRequestResponse) => req.source === 'GENERAL_BUTTON'),
    [demoRequests]
  );

  const promoterSourcedRequests = useMemo(() =>
    demoRequests.filter((req: DemoRequestResponse) => req.source === 'FEATURED_REVIEW'),
    [demoRequests]
  );

  const filteredRequests = useMemo(() => {
    const baseList = requestSubTab === 'book_demo' ? bookDemoRequests : promoterSourcedRequests;
    return baseList.filter((req: DemoRequestResponse) => {
      const matchesStatus = requestStatusFilter === 'all' || req.status === requestStatusFilter;
      const term = requestSearchTerm.toLowerCase();
      const matchesSearch = term === '' ||
        req.name.toLowerCase().includes(term) ||
        req.email.toLowerCase().includes(term) ||
        req.company.toLowerCase().includes(term) ||
        (req.phone || '').toLowerCase().includes(term);
      return matchesStatus && matchesSearch;
    });
  }, [bookDemoRequests, promoterSourcedRequests, requestSubTab, requestStatusFilter, requestSearchTerm]);

  const getStatusBadge = (status: string) => {
    const config: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
      NEW: { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', icon: <Clock className="w-3 h-3" /> },
      CONTACTED: { bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-700', icon: <Phone className="w-3 h-3" /> },
      SCHEDULED: { bg: 'bg-purple-50 border-purple-200', text: 'text-purple-700', icon: <Calendar className="w-3 h-3" /> },
      COMPLETED: { bg: 'bg-green-50 border-green-200', text: 'text-green-700', icon: <CheckCircle className="w-3 h-3" /> },
      CANCELLED: { bg: 'bg-gray-50 border-gray-200', text: 'text-gray-500', icon: <XCircle className="w-3 h-3" /> },
    };
    const c = config[status] || config.NEW;
    const labelMap: Record<string, string> = {
      NEW: t('vendorPage.myPage.requests.status.new'),
      CONTACTED: t('vendorPage.myPage.requests.status.contacted'),
      SCHEDULED: t('vendorPage.myPage.requests.status.scheduled'),
      COMPLETED: t('vendorPage.myPage.requests.status.completed'),
      CANCELLED: t('vendorPage.myPage.requests.status.cancelled'),
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${c.bg} ${c.text}`}>
        {c.icon}
        {labelMap[status] || status}
      </span>
    );
  };

  const handleAssign = async (requestId: string, teamMemberId: string, teamMemberName: string) => {
    try {
      await assignDemoRequest({ id: requestId, teamMemberId, teamMemberName }).unwrap();
      setAssignDropdownId(null);
    } catch (err) {
      console.error('Failed to assign demo request:', err);
    }
  };

  const handleStatusChange = async (requestId: string, status: string) => {
    try {
      await updateDemoRequestStatus({ id: requestId, status }).unwrap();
      setStatusDropdownId(null);
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const renderRequestRow = (req: DemoRequestResponse) => (
    <div key={req.id} className="px-5 py-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-semibold text-gray-900 text-sm">{req.name}</h4>
              <span className="text-gray-300">·</span>
              <span className="text-sm text-gray-500">{req.email}</span>
              {req.phone && (
                <>
                  <span className="text-gray-300">·</span>
                  <span className="text-sm text-gray-500">{req.phone}</span>
                </>
              )}
              <span className="text-gray-300">·</span>
              <span className="text-sm text-gray-500">{req.company}</span>
            </div>

            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-xs text-gray-400">
                {new Date(req.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
              {getStatusBadge(req.status)}
              {req.promoterName && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 border border-purple-200 rounded-full text-xs font-medium text-purple-700">
                  <Users className="w-3 h-3" />
                  {req.promoterName}
                </span>
              )}
              {req.assignedToName && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 border border-indigo-100 rounded-full text-xs font-medium text-indigo-700">
                  <UserPlus className="w-3 h-3" />
                  {req.assignedToName}
                </span>
              )}
              {req.message && (
                <span className="text-xs text-gray-400 truncate max-w-xs" title={req.message}>
                  — {req.message}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="relative">
            {req.assignedToTeamMemberId ? (
              <span className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-400 bg-gray-50 border border-gray-200 rounded-lg cursor-default">
                <Check className="w-3.5 h-3.5 text-green-500" />
                {t('vendorPage.myPage.requests.assignedTo')}
              </span>
            ) : (
              <button
                onClick={() => setAssignDropdownId(assignDropdownId === req.id ? null : req.id)}
                disabled={isAssigning}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAssigning && assignDropdownId === req.id ? (
                  <Loader className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <UserPlus className="w-3.5 h-3.5" />
                )}
                {t('vendorPage.myPage.requests.assignToCsm')}
                <ChevronDown className="w-3 h-3" />
              </button>
            )}

            {assignDropdownId === req.id && !req.assignedToTeamMemberId && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-20">
                {teamMembers.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-gray-500">
                    {t('vendorPage.myPage.requests.noTeamMembers')}
                  </div>
                ) : (
                  teamMembers.map((member) => (
                    <button
                      key={member.id}
                      onClick={() => handleAssign(req.id, member.id, `${member.firstName} ${member.lastName}`)}
                      disabled={isAssigning}
                      className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors flex items-center justify-between text-gray-700 disabled:opacity-50"
                    >
                      <span>{member.firstName} {member.lastName}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="relative">
            <button
              onClick={() => setStatusDropdownId(statusDropdownId === req.id ? null : req.id)}
              disabled={isUpdatingStatus}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              {isUpdatingStatus && statusDropdownId === req.id ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <MoreHorizontal className="w-4 h-4" />
              )}
            </button>

            {statusDropdownId === req.id && (
              <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-20">
                <div className="px-3 py-1.5 text-xs font-medium text-gray-400 uppercase tracking-wider">
                  {t('vendorPage.myPage.requests.changeStatus')}
                </div>
                {(['NEW', 'CONTACTED', 'SCHEDULED', 'COMPLETED', 'CANCELLED'] as const).map((s) => {
                  const statusLabels: Record<string, string> = {
                    NEW: t('vendorPage.myPage.requests.status.new'),
                    CONTACTED: t('vendorPage.myPage.requests.status.contacted'),
                    SCHEDULED: t('vendorPage.myPage.requests.status.scheduled'),
                    COMPLETED: t('vendorPage.myPage.requests.status.completed'),
                    CANCELLED: t('vendorPage.myPage.requests.status.cancelled'),
                  };
                  return (
                    <button
                      key={s}
                      onClick={() => handleStatusChange(req.id, s)}
                      disabled={isUpdatingStatus}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 ${req.status === s ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                        }`}
                    >
                      {statusLabels[s]}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderPromoterRequests = () => {
    if (requestsLoading) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader className="w-8 h-8 animate-spin text-green-600" />
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Sub-tab navigation */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          <button
            onClick={() => { setRequestSubTab('book_demo'); setRequestSearchTerm(''); setRequestStatusFilter('all'); }}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${requestSubTab === 'book_demo'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            {t('vendorPage.myPage.requests.bookDemoTab')} ({bookDemoRequests.length})
          </button>
          <button
            onClick={() => { setRequestSubTab('promoter_requests'); setRequestSearchTerm(''); setRequestStatusFilter('all'); }}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${requestSubTab === 'promoter_requests'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            {t('vendorPage.myPage.requests.promoterRequestsTab')} ({promoterSourcedRequests.length})
          </button>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100">
            <div className="text-2xl font-bold text-gray-900">{demoStats?.total || 0}</div>
            <div className="text-xs text-gray-500 mt-1">{t('vendorPage.myPage.requests.total')}</div>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-blue-100">
            <div className="text-2xl font-bold text-blue-600">{demoStats?.new || 0}</div>
            <div className="text-xs text-blue-600 mt-1">{t('vendorPage.myPage.requests.new')}</div>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-yellow-100">
            <div className="text-2xl font-bold text-yellow-600">{demoStats?.contacted || 0}</div>
            <div className="text-xs text-yellow-600 mt-1">{t('vendorPage.myPage.requests.contacted')}</div>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-purple-100">
            <div className="text-2xl font-bold text-purple-600">{demoStats?.scheduled || 0}</div>
            <div className="text-xs text-purple-600 mt-1">{t('vendorPage.myPage.requests.scheduled')}</div>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-green-100">
            <div className="text-2xl font-bold text-green-600">{demoStats?.completed || 0}</div>
            <div className="text-xs text-green-600 mt-1">{t('vendorPage.myPage.requests.completed')}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {requestSubTab === 'book_demo' ? t('vendorPage.myPage.requests.bookDemoTab') : t('vendorPage.myPage.requests.promoterRequestsTab')} ({filteredRequests.length})
            </h3>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder={t('vendorPage.myPage.requests.searchPlaceholder')}
                  value={requestSearchTerm}
                  onChange={(e) => setRequestSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-64"
                />
              </div>
              <select
                value={requestStatusFilter}
                onChange={(e) => setRequestStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="all">{t('vendorPage.myPage.requests.allStatuses')}</option>
                <option value="NEW">{t('vendorPage.myPage.requests.status.new')}</option>
                <option value="CONTACTED">{t('vendorPage.myPage.requests.status.contacted')}</option>
                <option value="SCHEDULED">{t('vendorPage.myPage.requests.status.scheduled')}</option>
                <option value="COMPLETED">{t('vendorPage.myPage.requests.status.completed')}</option>
                <option value="CANCELLED">{t('vendorPage.myPage.requests.status.cancelled')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Request list */}
        {filteredRequests.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-200">
            <Phone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-700 mb-1">{t('vendorPage.myPage.requests.noRequestsTitle')}</h3>
            <p className="text-sm text-gray-500">
              {t('vendorPage.myPage.requests.noRequestsHelp')}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 divide-y divide-gray-100">
            {filteredRequests.map((req: DemoRequestResponse) => renderRequestRow(req))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('vendorPage.myPage.title')}</h1>
        <div className="flex items-center space-x-4">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${pageIsDraft ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>
            {pageIsDraft ? t('vendorPage.myPage.draft') : t('vendorPage.myPage.published')}
          </span>
          {canEdit && (
            <button
              onClick={handleTogglePageStatus}
              disabled={pageStatusSaving}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 ${pageIsDraft
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                }`}
            >
              {pageStatusSaving ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : pageIsDraft ? (
                <><Globe className="w-4 h-4" /><span>{t('vendorPage.myPage.publish')}</span></>
              ) : (
                <><FileText className="w-4 h-4" /><span>{t('vendorPage.myPage.switchToDraft')}</span></>
              )}
            </button>
          )}
          <div className="flex items-center space-x-3">
            <button
              onClick={handleCopyLink}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${copiedLink
                  ? 'bg-green-600 text-white'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
            >
              {copiedLink ? (
                <>
                  <Check className="w-4 h-4" />
                  <span>{t('vendorPage.myPage.copied')}</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span>{t('vendorPage.myPage.copyLink')}</span>
                </>
              )}
            </button>

            <button
              onClick={() => window.open(publicPageUrl, '_blank')}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Eye className="w-4 h-4" />
              <span>{t('vendorPage.myPage.viewPublicPage')}</span>
            </button>

          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center space-x-2">
                <Building className="w-4 h-4" />
                <span>{t('vendorPage.myPage.tabs.overview')}</span>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'reviews'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center space-x-2">
                <Star className="w-4 h-4" />
                <span>{t('vendorPage.myPage.tabs.reviews')} ({allReviews.length})</span>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('usecases')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'usecases'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center space-x-2">
                <Target className="w-4 h-4" />
                <span>{t('vendorPage.myPage.tabs.usecases')} ({useCases.length})</span>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('public')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'public'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center space-x-2">
                <Globe className="w-4 h-4" />
                <span>{t('vendorPage.myPage.tabs.public')}</span>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('requests')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'requests'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4" />
                <span>{t('vendorPage.myPage.tabs.requests')}{demoStats?.total ? ` (${demoStats.total})` : ''}</span>
              </div>
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'reviews' && renderReviews()}
          {activeTab === 'usecases' && renderUseCases()}
          {activeTab === 'public' && renderPublicPage()}
          {activeTab === 'requests' && renderPromoterRequests()}
        </div>
      </div>

      <VideoModal
        isOpen={!!playingVideoUrl}
        onClose={() => {
          setPlayingVideoUrl(null);
          setPlayingVideoTitle(undefined);
          setPlayingVideoDescription(undefined);
          setPlayingVideoTags([]);
        }}
        videoUrl={playingVideoUrl || ''}
        title={playingVideoTitle}
        description={playingVideoDescription}
        tags={playingVideoTags}
      />
    </div>
  );
};

export default MyPage;